/**
 * EmailTagHelp — the tag palette, the undeclared-tag warning, and a sample preview.
 * =================================================================================================
 *
 * ⭐ Shown only for the Email category, beneath the subtype and subject fields.
 *
 * ⛔ THE WARNING IS THE POINT, not the palette. `renderTemplate` REFUSES to send a template that
 * uses a tag the registry does not declare, or one the caller supplies no value for — and that
 * refusal happens at send time, in a log line, long after the author has walked away. Catching it
 * here means the mistake is visible while the person who made it is still looking at it.
 *
 * ⚠️ This is a WARNING, never a block. The authoritative check is the renderer's, and a client that
 * refused to save would be a second rule to keep in step with the first. Two validators disagreeing
 * is worse than one in the wrong place.
 *
 * ⓘ Sample values are illustrative only. The real ones come from recipientController at send time,
 * and `booksSentence` in particular is COMPOSED in code because a template cannot branch.
 */
import React, { useMemo } from 'react';
import { Label, Message, Table } from 'semantic-ui-react';

const TAG = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;

/** Illustrative values, matching the shape recipientController actually supplies. */
const SAMPLE = {
  name: 'Mary Jo',
  booksSentence: 'Your account is set up and The Ashes of Madonna 13 is waiting for you.',
  books: 'The Ashes of Madonna 13',
  bookCount: '1',
  listenUrl: 'https://authorator-ai.com/listen',
  keyLabel: 'Sister Girls',
  bookTitle: 'The Ashes of Madonna 13',
  resetUrl: 'https://authorator-ai.com/reset?t=sample',
  expiresIn: '2 hours',
  expiresAt: '10 January 2027',
};

function tagsIn(str) {
  const out = new Set();
  String(str || '').replace(TAG, (_, name) => { out.add(name); return ''; });
  return [...out];
}

/** ⓘ Mirrors renderTemplate's own substitution closely enough to be useful, not to be authoritative. */
function fill(str) {
  return String(str || '').replace(TAG, (whole, name) => (
    Object.prototype.hasOwnProperty.call(SAMPLE, name) ? SAMPLE[name] : whole
  ));
}

/** Tags → readable text, for the preview. Deliberately crude; the real one lives server-side. */
function toText(html) {
  return String(html || '')
    .replace(/<\s*(script|style)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<a\b[^>]*href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\s*\/\s*a\s*>/gi,
      (_, href, text) => {
        const label = text.replace(/<[^>]+>/g, '').trim();
        return (!label || label === href) ? href : label + ' (' + href + ')';
      })
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|h[1-6]|li|tr|blockquote)\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi, (w, b) => {
      const named = {
        amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
        mdash: '—', ndash: '–', hellip: '…', rsquo: '’', lsquo: '‘',
        ldquo: '“', rdquo: '”',
      };
      if (b[0] === '#') {
        const code = (b[1] === 'x' || b[1] === 'X') ? parseInt(b.slice(2), 16) : parseInt(b.slice(1), 10);
        return Number.isFinite(code) ? String.fromCodePoint(code) : w;
      }
      return named[b.toLowerCase()] || w;
    })
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** ⓘ A plain row wrapper, so this component needs no Grid import at its use site. */
const FormRow = ({ children }) => (
  <div className="form-row" style={{ justifyContent: 'flex-start', margin: '0.5rem 0 0 13px' }}>
    {children}
  </div>
);

const EmailTagHelp = ({ subtype, subtypes, subject, body }) => {
  const def = useMemo(
    () => (subtypes || []).find((s) => s.subtype === subtype) || null,
    [subtypes, subtype],
  );

  const used = useMemo(() => tagsIn(subject).concat(tagsIn(body))
    .filter((v, i, a) => a.indexOf(v) === i), [subject, body]);

  if (!subtype) {
    return (
      <FormRow>
        <Message info size="small" style={{ width: '100%' }}>
          Choose an email purpose above to see the tags this message may use.
        </Message>
      </FormRow>
    );
  }

  if (!def) {
    return (
      <FormRow>
        <Message warning size="small" style={{ width: '100%' }}>
          <Message.Header>Unknown purpose</Message.Header>
          <p>
            &quot;{subtype}&quot; is not in the server&rsquo;s registry, so nothing would ever send
            this template. Pick one from the list.
          </p>
        </Message>
      </FormRow>
    );
  }

  const declared = def.tags || [];
  const undeclared = used.filter((t) => !declared.includes(t));

  return (
    <FormRow>
      <div style={{ width: '100%' }}>
        {/* The palette. ⓘ Click-to-copy is deliberately not implemented: the names are short and a
            half-working copy button is worse than none. */}
        <div style={{ margin: '0 0 0.5rem' }}>
          <strong style={{ marginRight: '0.5rem' }}>Tags you may use:</strong>
          {declared.map((t) => (
            <Label key={t} basic size="small" style={{ marginBottom: '0.25rem' }}>
              {'{{' + t + '}}'}
            </Label>
          ))}
        </div>

        {/*
          ⛔ THE WARNING THAT EARNS THIS COMPONENT. An undeclared tag makes renderTemplate refuse at
          SEND time — so without this the author learns about it from a log line, days later, if at
          all.
        */}
        {undeclared.length > 0 && (
          <Message negative size="small">
            <Message.Header>
              {undeclared.length === 1 ? 'This tag is not allowed here' : 'These tags are not allowed here'}
            </Message.Header>
            <p>
              {undeclared.map((t) => '{{' + t + '}}').join(', ')}
              {' — '}
              not declared for <strong>{def.label}</strong>. The message will <strong>refuse to
              send</strong> rather than deliver a half-filled template.
            </p>
          </Message>
        )}

        {/* The preview. ⭐ The only way to see what a reader sees. */}
        {(subject || body) && (
          <Table basic="very" compact size="small" style={{ marginTop: '0.5rem' }}>
            <Table.Body>
              <Table.Row>
                <Table.Cell collapsing><strong>Subject</strong></Table.Cell>
                <Table.Cell>{fill(subject) || <em>(none — the send will refuse)</em>}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell collapsing verticalAlign="top"><strong>Preview</strong></Table.Cell>
                <Table.Cell>
                  <pre style={{
                    whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit', fontSize: '0.95rem',
                  }}
                  >
                    {toText(fill(body)) || '(empty)'}
                  </pre>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        )}

        <p className="lp-small" style={{ color: '#767676', marginTop: '0.25rem' }}>
          Sample values shown. ⓘ Real ones are supplied when the message is sent.
        </p>
      </div>
    </FormRow>
  );
};

export default EmailTagHelp;
